import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const hoofdItems = [
  {
    to: '/beregening/houders',
    label: 'Houders',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    )
  },
  {
    to: '/beregening/putten',
    label: 'Putten',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
      </svg>
    )
  },
  {
    to: '/beregening/inrichtingen',
    label: 'Inrichtingen',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/>
        <path d="M5 21V7l8-4v18"/>
        <path d="M19 21V11l-6-4"/>
        <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/>
      </svg>
    )
  },
  {
    to: '/beregening/toezicht',
    label: 'Toezicht & handhaving',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    )
  },
];

const secundaireItems = [
  {
    to: '/beheertools',
    label: 'Beheertools',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    )
  },
];

// Mapping van URL-segment naar module-label (voor de subtitel onder de branding)
const MODULE_LABELS = {
  beregening: 'Beregening',
  beheertools: 'Beheertools',
  // Bij toekomstige doelen: bronbemaling, drainage, sanering, etc.
};

function huidigeModule(pathname) {
  const segment = pathname.split('/').filter(Boolean)[0]; // eerste path-segment
  return MODULE_LABELS[segment] || null;
}

export default function Sidebar() {
  const location = useLocation();
  const moduleNaam = huidigeModule(location.pathname);
  const { gebruiker, logout } = useAuth();
  return (
    <nav className="sidebar">
      <NavLink to="/" className="sidebar-back" title="Naar startpagina">
        ← Startpagina
      </NavLink>
      <div className="sidebar-brand">Grondwaterregister</div>
      {moduleNaam && <div className="sidebar-modulenaam">{moduleNaam}</div>}
      <div className="sidebar-section">
        {hoofdItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
      <div className="sidebar-spacer" />
      <div className="sidebar-section">
        {secundaireItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}
        {gebruiker?.rol === 'beheerder' && (
          <NavLink
            to="/beheer/gebruikers"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Gebruikers</span>
          </NavLink>
        )}
      </div>

      <div style={{
        borderTop: '1px solid var(--border)',
        marginTop: 8,
        paddingTop: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}>
        <NavLink
          to="/account"
          style={{
            fontSize: 13,
            color: 'var(--text-soft)',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title="Account & beveiliging"
        >
          {gebruiker?.naam}
        </NavLink>
        <button
          className="btn btn-ghost"
          onClick={logout}
          style={{ fontSize: 13, padding: '4px 10px', flexShrink: 0 }}
          title="Uitloggen"
        >
          Uitloggen
        </button>
      </div>
    </nav>
  );
}
